import { constructPath } from '@oskecare/fe-utilities/scripts/helpers.js';

/**
 * Module for handling thrown errors (including PromiseRejections from LitElement's lifecycle errors).
 * All error are logged. RequestError objects with 401 status are handled by AuthManager.
 *
 * This module replaces previous complicated mechanism of processing the errors in OrangeL1Widget and OrangeL2Widget
 * classes, these now only delay certain errors until error message was rendered and then throw them as they would
 * any other unexpected error. All errors will be caught and processed by the ErrorHandler.
 *
 * @property {Logger} _logger
 * @property {AuthManager} _authManager
 * @property {string} _basePath
 */
export default class ErrorHandler {
  constructor({ logger, authManager, config }) {
    this._logger = logger;
    this._authManager = authManager;
    this._basePath = config.basePath;
  }

  /**
   * Register event listeners for "error" and "unhandledrejection" events. The former handler errors normally thrown
   * in regular JS code, while the latter specifically catches errors thrown in LitElement lifecycle methods.
   * In both cases we check for 401 RequestErrors and log the error object.
   */
  initialize() {
    window.addEventListener('error', async errorEvent => {
      if (errorEvent instanceof ErrorEvent) {
        const { error } = errorEvent;
        if (error instanceof Error) {
          await this._handleUnauthorizedAccess(error);
          await this._logger.logErrorObject(error);
          this._handleFatalError(error);
        } else {
          this._logger.critical(`ErrorEvent without Error object: "${error}"`, 'uncaught-error');
        }

        errorEvent.preventDefault();
      }
    });

    // Errors thrown in LitElement lifecycle methods end up as PromiseRejectionEvents
    // See https://lit.dev/docs/components/lifecycle/#errors-in-the-update-cycle
    window.addEventListener('unhandledrejection', async promiseRejectionEvent => {
      const error = promiseRejectionEvent.reason;
      if (error instanceof Error) {
        await this._handleUnauthorizedAccess(error);
        await this._logger.logErrorObject(error);
        this._handleFatalError(error);
      } else {
        this._logger.critical(
          `PromiseRejectionEvent without Error object: "${error}"`,
          'uncaught-error',
        );
      }

      promiseRejectionEvent.preventDefault();
    });
  }

  /**
   * Check error message if it is 401 error,
   * and redirect user to appropriate page (e.g. SSO login page) if so
   *
   * @param {RequestError} error
   * @return {Promise<void>}
   */
  async _handleUnauthorizedAccess(error) {
    if (error.name === 'RequestError' && error.responseStatus === 401) {
      // Check if auth endpoint concurs that user doesn't have a valid token
      const loginStatus = await this._authManager.verifyLogin();

      if (!loginStatus) {
        // If they agree, user is redirected to SSO
        this._authManager.retryLogin();
      } else {
        // Otherwise the 401 error was erroneous
        const method = error?.requestLogData?.request?.method;
        const url = error?.requestLogData?.request?.url;
        this._logger.critical(
          `Request ${method} ${url} returned false 401.`,
          'infra-error-handler',
        );
      }
    }
  }

  /**
   * Check if the error is a fatal request error (other than 401), and redirect to
   * error page if so.
   *
   * @param {RequestError} error
   * @private
   */
  _handleFatalError(error) {
    if (error.name === 'RequestError' && error.isFatal && error.responseStatus !== 401) {
      const errorPath = constructPath('chyba', this._basePath);
      window.location.assign(errorPath);
    }
  }
}
